import type { VoiceCreationLocation } from "../../interfaces/VoiceProductCreateObject";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CreateProductModalState,
  DraftMediaUploadState,
  EssentialParameters,
  DescriptiveParameters,
  StrategicParameters,
  KeyAttributes,
  InfoDraft,
} from "../../interfaces/ProductObject";
import { releaseAllDraftMedia } from "../../services/helpers/ProductDraftMediaHelper";
import { resetAllState } from "./resetAllState";

const initialState: CreateProductModalState = {
  productId: undefined,
  infoDraft: { template: "blank", blocks: [], sponsor: null, loaded: true },
  essentials: {
    media: [],
    listingType: "",
    age: "",
    externalListingSummary: "",
    sharedAttributes: {},
  },
  description: {
    title: "",
    category: "",
    description: "",
  },
  keyAttributes: {
    mandatoryAttributes: {},
    optionalAttributes: {},
  },
  strategy: {
    askPrice: "",
    originalPrice: "",
    suggestion: {},
    contactMode: 'MANUAL',
    isNegotiable: false,
    rentalPricing: {},
    depositAmount: "",
    displayPrice: true,
    // UI-only: drives whether a listing inherits its business's coordinates.
    // Resolved in EditorView/ProductConvertor — never spread into a backend payload.
    useBusinessLocation: true,
  },
  meta: {
    currentStep: 1,
    viewMode: "editing",
  },
};

const createProductSlice = createSlice({
  name: "createProduct",
  initialState,
  reducers: {
    initializeFromVoiceDraft(state, action: PayloadAction<CreateProductModalState>) {
      releaseAllDraftMedia(state.essentials.media);
      return { ...initialState, ...action.payload, productId: undefined };
    },
    resolveVoiceCreationIssue(state, action: PayloadAction<string>) {
      if (state.voiceCreation && !state.voiceCreation.resolvedIssues.includes(action.payload)) state.voiceCreation.resolvedIssues.push(action.payload);
    },
    setVoiceCreationLocation(state, action: PayloadAction<VoiceCreationLocation>) {
      if (state.voiceCreation) state.voiceCreation.location = action.payload;
    },
    updateProductId(state, action: PayloadAction<string>) {
      state.productId = action.payload;
    },
    /* ---------------- Essentials ---------------- */
    updateEssentials(
      state,
      action: PayloadAction<Partial<EssentialParameters>>
    ) {
      state.essentials = { ...state.essentials, ...action.payload };
    },
    updateExternalListingSummary(
      state,
      action: PayloadAction<string>
    ) {
      state.essentials.externalListingSummary = action.payload;
    },

    /**
     * Eager-upload progress for one "new" draft item. No-ops when the item
     * was removed (or the draft reset) while its upload was in flight — the
     * uploaded bytes become orphans, cleaned by the backend's storage GC.
     */
    setDraftMediaUpload(
      state,
      action: PayloadAction<{ id: string; upload: DraftMediaUploadState }>
    ) {
      const item = state.essentials.media.find(
        (m) => m.status === "new" && m.id === action.payload.id
      );
      if (item && item.status === "new") {
        item.upload = action.payload.upload;
      }
    },

    updateSharedAttributes(
      state,
      action: PayloadAction<Record<string, string | string[]>>
    ) {
      state.essentials.sharedAttributes = {
        ...state.essentials.sharedAttributes,
        ...action.payload,
      };
    },

    /* ---------------- Description ---------------- */
    updateDescription(
      state,
      action: PayloadAction<Partial<DescriptiveParameters>>
    ) {
      state.description = { ...state.description, ...action.payload };
    },

    /* ---------------- Key Attributes ---------------- */
    updateKeyAttributes(
      state,
      action: PayloadAction<Partial<KeyAttributes>>
    ) {
      state.keyAttributes = {
        ...state.keyAttributes,
        ...action.payload,
      };
    },

    updateMandatoryAttributes(
      state,
      action: PayloadAction<Record<string, string>>
    ) {
      state.keyAttributes.mandatoryAttributes = action.payload || {};
    },

    updateOptionalAttributes(
      state,
      action: PayloadAction<
        Record<string, { key: string; value: string }>
      >
    ) {
      state.keyAttributes.optionalAttributes = action.payload || {};
    },

    /* ---------------- Strategy ---------------- */
    updateStrategy(
      state,
      action: PayloadAction<Partial<StrategicParameters>>
    ) {
      state.strategy = { ...state.strategy, ...action.payload };
    },

    updateSuggestedPrice(
      state,
      action: PayloadAction<Partial<StrategicParameters["suggestion"]>>
    ) {
      state.strategy.suggestion = {
        ...state.strategy.suggestion,
        ...action.payload,
      };
    },

    /* ---------------- Info listing ---------------- */
    updateInfoDraft(state, action: PayloadAction<Partial<InfoDraft>>) {
      state.infoDraft = {
        ...(state.infoDraft ?? { template: "blank", blocks: [], sponsor: null, loaded: true }),
        ...action.payload,
      };
    },

    /* ---------------- Meta ---------------- */
    setStep(state, action: PayloadAction<number>) {
      state.meta.currentStep = action.payload;
    },

    setViewMode(state, action: PayloadAction<"editing" | "preview">) {
      state.meta.viewMode = action.payload;
    },

    /* ---------------- Lifecycle ---------------- */
    // NOTE: loadProductForEdit / resetToInitialState free the previous draft's
    // local resources (object URLs + DraftMediaFileStore entries). Technically
    // a side effect inside a reducer, but it's idempotent, touches no state,
    // and doing it here guarantees no leak regardless of which of the many
    // call sites dispatched the reset.
    loadProductForEdit(
      state,
      action: PayloadAction<CreateProductModalState>
    ) {
      releaseAllDraftMedia(state.essentials.media);
      return action.payload;
    },

    resetToInitialState(state) {
      releaseAllDraftMedia(state.essentials.media);
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetAllState, (state) => {
      releaseAllDraftMedia(state.essentials.media);
      return initialState;
    });
  },
});

export const {
  initializeFromVoiceDraft,
  resolveVoiceCreationIssue,
  setVoiceCreationLocation,
  updateProductId,
  updateEssentials,
  setDraftMediaUpload,
  updateExternalListingSummary,
  updateSharedAttributes,
  updateDescription,
  updateKeyAttributes,
  updateMandatoryAttributes,
  updateOptionalAttributes,
  updateStrategy,
  updateSuggestedPrice,
  updateInfoDraft,
  setStep,
  setViewMode,
  loadProductForEdit,
  resetToInitialState,
} = createProductSlice.actions;

export default createProductSlice.reducer;
